import { MyProjects, UserNextBestActions } from '@platform/client';
import { DatePicker, Modal, ModalCallToAction } from '@platform/components';
import { useQueryClient } from '@tanstack/react-query';
import cx from 'classix';
import { useState, createContext, useContext, useCallback, ReactNode } from 'react';
import { AccountTypeEnum } from '@platform/client/schemas';

export enum UpdateProjectDatesModalStage {
  Update = 'update',
  Confirmation = 'confirmation',
  Error = 'error',
}

type UpdateProjectDatesModalProps = (
  | {
      userType: typeof AccountTypeEnum.organization;
      isTeam: true;
      professionalFirstName?: never;
    }
  | {
      userType: typeof AccountTypeEnum.organization;
      isTeam: false;
      professionalFirstName: string;
    }
  | {
      userType: typeof AccountTypeEnum.experteer;
      isTeam: boolean;
      organizationName: string;
    }
) & {
  id: number;
  startDate: Date;
  endDate: Date;
};

interface UpdateProjectDatesModalContext {
  isOpen: boolean;
  openUpdateProjectDatesModal: (props: UpdateProjectDatesModalProps) => void;
  closeUpdateProjectDatesModal: () => void;
  modalStage: UpdateProjectDatesModalStage;
  setModalStage: (stage: UpdateProjectDatesModalStage) => void;
  modalProps?: UpdateProjectDatesModalProps;
}

export const UpdateProjectDatesModalContext = createContext<UpdateProjectDatesModalContext>({
  isOpen: false,
  openUpdateProjectDatesModal: () => {},
  closeUpdateProjectDatesModal: () => {},
  modalStage: UpdateProjectDatesModalStage.Update,
  setModalStage: () => {},
});

export const UpdateProjectDatesModalProvider = ({ children }: { children: ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [modalStage, setModalStage] = useState<UpdateProjectDatesModalStage>(
    UpdateProjectDatesModalStage.Update,
  );
  const [modalProps, setModalProps] = useState<UpdateProjectDatesModalProps | undefined>(undefined);

  const openUpdateProjectDatesModal = useCallback((props: UpdateProjectDatesModalProps) => {
    setIsOpen(true);
    setModalProps(props);
    setModalStage(UpdateProjectDatesModalStage.Update);
  }, []);

  const closeUpdateProjectDatesModal = useCallback(() => {
    setIsOpen(false);
    setModalProps(undefined);
    setModalStage(UpdateProjectDatesModalStage.Update);
  }, []);

  return (
    <UpdateProjectDatesModalContext.Provider
      value={{
        isOpen,
        openUpdateProjectDatesModal,
        closeUpdateProjectDatesModal,
        modalStage,
        setModalStage,
        modalProps,
      }}
    >
      {children}
      {isOpen && <UpdateProjectDatesModal />}
    </UpdateProjectDatesModalContext.Provider>
  );
};

const UpdateProjectDatesModal = () => {
  const { isOpen, closeUpdateProjectDatesModal, modalStage, setModalStage, modalProps } =
    useContext(UpdateProjectDatesModalContext);
  const [projectStartDate, setProjectStartDate] = useState<Date | null>(
    modalProps ? new Date(modalProps.startDate) : null,
  );
  const [projectEndDate, setProjectEndDate] = useState<Date | null>(
    modalProps ? new Date(modalProps.endDate) : null,
  );
  const [dateErrorMessage, setDateErrorMessage] = useState<string | undefined>();

  const modalOnClose = () => {
    setDateErrorMessage(undefined);
    closeUpdateProjectDatesModal();
  };

  const queryClient = useQueryClient();
  const updateProjectDatesMutation = MyProjects.useUpdateProjectDates({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: MyProjects.getGetOrgUserMyProjectsQueryKey() });
        queryClient.invalidateQueries({ queryKey: MyProjects.getGetProUserMyProjectsQueryKey() });
        queryClient.invalidateQueries({
          queryKey: UserNextBestActions.getGetUserNextBestActionsQueryKey(),
        });
        setModalStage(UpdateProjectDatesModalStage.Confirmation);
      },
      onError: () => {
        setModalStage(UpdateProjectDatesModalStage.Error);
      },
    },
  });

  if (!isOpen || !modalProps) {
    return null;
  }

  const validateUserEntry = (startDate: Date, endDate: Date) => {
    if (!startDate || !endDate) {
      setDateErrorMessage('Please select both a start and end date');
      return;
    }
    if (endDate < startDate) {
      setDateErrorMessage('End date must be after start date');
      return;
    }
    setDateErrorMessage(undefined);
    return;
  };

  const updateProjectDates = () => {
    if (projectStartDate && projectEndDate) {
      updateProjectDatesMutation.mutate({
        data: {
          project_id: modalProps?.id,
          start_date: projectStartDate.toISOString(),
          end_date: projectEndDate.toISOString(),
        },
      });
    }
  };

  const getPrimaryCTA = (modalStage: UpdateProjectDatesModalStage): ModalCallToAction => {
    switch (modalStage) {
      case 'update':
        return {
          text: 'Update',
          callback: () => {
            updateProjectDates();
          },
          buttonVariant: 'primary',
          isDisabled: () => {
            return !!dateErrorMessage;
          },
        };
      case 'confirmation':
      case 'error':
        return {
          text: 'Close',
          callback: () => {
            modalOnClose();
          },
        };
      default:
        throw new Error('Invalid modal stage');
    }
  };

  const getSecondaryCTA = (
    modalStage: UpdateProjectDatesModalStage,
  ): ModalCallToAction | undefined => {
    if (modalStage === 'update') {
      return {
        text: 'Cancel',
        callback: modalOnClose,
      };
    }
  };

  const otherSideHeaderText =
    modalProps.userType === 'experteer' ? modalProps.organizationName
    : modalProps.isTeam ? 'the experteer team'
    : modalProps.professionalFirstName;

  return (
    <Modal
      isOpen={isOpen}
      onClose={modalOnClose}
      headerDivision='divider'
      variant='dialog'
      title='Update Project Dates'
      primaryCTA={getPrimaryCTA(modalStage)}
      secondaryCTA={getSecondaryCTA(modalStage)}
      ignoreExtraPadding
    >
      {modalStage === 'update' && (
        <div className='flex flex-col gap-4 items-center max-w-[500px]'>
          <div className='text-center'>
            {`Update your start or end date below, we'll let ${otherSideHeaderText} know you've updated them.`}
          </div>
          <div className='flex flex-col gap-6 w-[80%]'>
            <DatePicker
              label='Start date'
              initialDate={projectStartDate ?? new Date()}
              onChange={(newStartDate) => {
                setProjectStartDate(newStartDate as Date);
                validateUserEntry(newStartDate, projectEndDate as Date);
              }}
              additionalClassNames='w-full'
            />
            <DatePicker
              label='End date'
              initialDate={projectEndDate ?? new Date()}
              onChange={(newEndDate) => {
                setProjectEndDate(newEndDate as Date);
                validateUserEntry(projectStartDate as Date, newEndDate);
              }}
              additionalClassNames={cx(
                'w-full',
                `${dateErrorMessage ? 'ring-error-shadow-xs' : ''}`,
              )}
            />
          </div>
          {dateErrorMessage && <p className='text-error-500 text-sm'>{dateErrorMessage}</p>}
        </div>
      )}
      {modalStage === 'confirmation' && (
        <div className='flex flex-col gap-4 items-center max-w-[500px]'>
          <div className='text-center'>
            Great, we have updated your project dates! If you need to make any further changes, you
            can do so on your My Projects page.
          </div>
        </div>
      )}
      {modalStage === 'error' && (
        <div className='flex flex-col gap-4 items-center max-w-[500px]'>
          <div className='text-center'>
            Oh no! Something went wrong and your new dates were not saved. Please try again or{' '}
            <a className='underline text-brand-100' href='mailto:support@movingworlds.org'>
              contact us for support
            </a>
            .
          </div>
        </div>
      )}
    </Modal>
  );
};

export default UpdateProjectDatesModal;
